import { browserAuthHeaders, BROWSER_SESSION_MARKER } from './browserAuthHeaders'
import i18n from '@/i18n'
import { normalizeLocale, defaultLocale } from '@/config/locales'
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { toCamelCase, toSnakeCase } from '@/utils/caseTransformer'
import { apiEvents } from '@/utils/apiEvents'
import { getEchoSocketId } from '@/services/echoClient'
import { getAccessToken, setAccessToken, getTenantId, getTokenExpiry, clearAuthData, clearPersistedAuthSlices, setTenantSuspended, setTenantExpired } from '@/services/tokenStorage'

declare module 'axios' {
  interface AxiosRequestConfig {
    // Optional enrichment must not report an outage of the POS itself.
    optionalService?: boolean
  }
}

interface StatusMeta {
  title: string
  message: string
}

interface RetryConfig extends InternalAxiosRequestConfig {
  __retryCount?: number
  // Set once the request body/params have been snake_cased; guards against
  // double-transforming a payload when a 503 retry replays the same config
  // through the request interceptor again (toSnakeCase is not idempotent).
  __caseTransformed?: boolean
}

const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options'])

const STATUS_META: Record<number, StatusMeta> = {
  429: { title: 'Too Many Requests', message: 'Please wait a moment and try again.' },
  500: { title: 'Server Error', message: 'An internal server error occurred. Our team has been notified.' },
  502: { title: 'Bad Gateway', message: 'The server is temporarily unavailable. Please try again shortly.' },
  503: { title: 'Service Unavailable', message: 'The server is under maintenance. Please try again in a few moments.' },
  504: { title: 'Gateway Timeout', message: 'The request timed out. Please check your connection and try again.' },
}

const MAX_RETRIES = 2
const RETRY_DELAY_MS = 3000

const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
})

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.headers['Accept-Language'] = normalizeLocale(i18n.language) ?? defaultLocale
    // Proactive expiry check. A tenant token has no refresh flow (Passport
    // personal access token only), so once the stored expiry has passed the
    // request can only answer 401. Fail it here, once, and tear the session
    // down — instead of letting a burst of parallel requests (and the POS
    // offline replay) hammer the API with a dead token.
    const expiresAt = getTokenExpiry()
    if (expiresAt !== null && Date.now() >= expiresAt) {
      handleUnauthenticatedSession()
      // Same shape as the reactive 401 rejection (`{ ...body, status }`) so
      // callers that branch on `error.data.errors` / `error.errorCode` see one
      // contract regardless of which path failed.
      return Promise.reject({ status: 401, message: 'Session expired. Please sign in again.' })
    }

    // Retries may carry headers from a previous credential. Rebuild both
    // channels; a stale Bearer header would override the current cookie.
    delete config.headers.Authorization
    delete config.headers['X-POS-CSRF']
    Object.assign(config.headers, browserAuthHeaders(getAccessToken()))
    const tenantId = getTenantId()
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId
    }
    // toOthers exclusion: carry the live Reverb socket id so the backend
    // BroadcastPayload can skip echoing our own mutations back to us.
    try {
      const socketId = getEchoSocketId()
      if (socketId) {
        config.headers['X-Socket-ID'] = socketId
      }
    } catch {
      // Realtime must never break API requests.
    }
    if (!(config as RetryConfig).__caseTransformed) {
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type']
        const converted = new FormData()
        for (const [key, value] of config.data.entries()) {
          const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase()
          converted.append(snakeKey, value as Blob | string)
        }
        config.data = converted
      } else if (config.data && typeof config.data === 'object') {
        config.data = toSnakeCase(config.data)
      }
      if (config.params && typeof config.params === 'object') {
        config.params = toSnakeCase(config.params)
      }
      ;(config as RetryConfig).__caseTransformed = true
    }
    return config
  },
  (error: unknown) => {
    return Promise.reject(error)
  }
)

let isRedirecting = false

/**
 * Tear the local session down and send the user to the login screen, keeping
 * the current page so they land back where they were. Guarded so a burst of
 * failing requests produces ONE redirect. Shared by the reactive 401 handler
 * and the proactive expiry check in the request interceptor.
 */
function handleUnauthenticatedSession(): void {
  if (isRedirecting) return

  isRedirecting = true
  clearAuthData()
  // tokenStorage is cleared above, but the persisted auth slice (user PII +
  // isAuthenticated) lives in redux-persist's localStorage keys. Drop it too,
  // preserving posSession so a mid-sale cart survives the re-login redirect.
  clearPersistedAuthSlices()

  const currentPath = window.location.pathname
  const excludedPaths = ['/login', '/signin', '/sessions', '/password']
  const isExcluded =
    currentPath === '/' || excludedPaths.some(path => currentPath.startsWith(path))

  if (!isExcluded) {
    localStorage.setItem('last_visited_page', JSON.stringify(currentPath))
    window.location.href = '/login'
  } else {
    isRedirecting = false
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function handleTenantErrorCodes(status: number, data: any): boolean {
  const errorCode = data?.errorCode
  const message = data?.message

  if (errorCode === 'TENANT_DATABASE_ERROR') {
    apiEvents.emit('error', {
      type: 'warning',
      message: message || 'Tenant database is being provisioned. Please wait...'
    })
    window.location.href = '/creating-tenant'
    return true
  }

  if (errorCode === 'TENANT_LIMIT_EXCEEDED') {
    apiEvents.emit('error', {
      type: 'warning',
      message: message || 'Your current plan limit has been reached. Please upgrade to continue.'
    })
    return true
  }

  if (errorCode === 'STORAGE_QUOTA_EXCEEDED') {
    apiEvents.emit('error', {
      type: 'warning',
      message: message || 'You have exceeded your storage quota. Please upgrade your plan or free up space.'
    })
    return true
  }

  return false
}

api.interceptors.response.use(
  (response) => {
    if (response.headers?.['x-browser-session'] === 'cookie') setAccessToken(BROWSER_SESSION_MARKER)
    if (response.data) {
      response.data = toCamelCase(response.data)
    }
    return response
  },
  async (error: AxiosError<{ message?: string; error_code?: string; errors?: Record<string, string[]> }>) => {
    if (axios.isCancel(error)) return Promise.reject(error)
    if (error.response) {
      const status = error.response.status
      const raw = error.response.data
      const data = (raw ? toCamelCase(raw) : raw) as Record<string, any> | undefined

      // Attach the HTTP status to every rejected body: RTK Query's base
      // query and useErrorHandler read it to categorize failures (a bare
      // body used to degrade every 422 to a generic "unknown" error and
      // drop the field-level errors map).
      const rejection =
        data && typeof data === 'object' && !Array.isArray(data)
          ? { ...data, status } as any
          : // An array/string body must not be spread: spreading an array yields
            // an object with numeric index keys and no `errors` map. Wrap it so
            // `status` still reaches RTK Query / useErrorHandler.
            { message: typeof data === 'string' ? data : undefined, data, status } as any

      if (handleTenantErrorCodes(status, data)) {
        return Promise.reject(rejection)
      }

      if (error.config?.optionalService && (status === 429 || status >= 500)) {
        return Promise.reject(rejection)
      }

      if (status === 503) {
        const config = error.config as RetryConfig
        const method = (config?.method || 'get').toLowerCase()
        // Only replay idempotent requests: a 503 may arrive after the server
        // already committed a mutation, so replaying a POST/PUT/PATCH/DELETE
        // would duplicate side effects (double POS sales/payments).
        if (config && IDEMPOTENT_METHODS.has(method)) {
          config.__retryCount = config.__retryCount || 0
          if (config.__retryCount < MAX_RETRIES) {
            config.__retryCount++
            await delay(RETRY_DELAY_MS)
            return api(config)
          }
        }
      }

      switch (status) {
        case 401: {
          const requestUrl = error.config?.url || ''
          // 2FA challenge endpoints (verify/status) reject with 401 on an
          // invalid or expired provisional token — do NOT wipe local auth
          // state, the user is mid-challenge and needs the provisional token
          // to retry. Let the login screen surface the error instead.
          if (requestUrl.includes('/two-factor/verify') || requestUrl.includes('/two-factor/status')) {
            return Promise.reject(rejection)
          }
          handleUnauthenticatedSession()
          break
        }

        case 403: {
          const requestUrl = error.config?.url || ''
          const isAdminSessionCheck = requestUrl.includes('/admin/auth/session')
          if (isAdminSessionCheck) {
            return Promise.reject(error)
          }
          if (data?.errorCode === 'TENANT_SUSPENDED') {
            setTenantSuspended(true)
            apiEvents.emit('error', {
              type: 'error',
              title: 'Account Suspended',
              message: data?.message || 'Your account has been suspended.'
            })
            window.location.href = '/tenant-suspended'
            break
          }
          if (data?.errorCode === 'TENANT_EXPIRED') {
            setTenantExpired(true)
            apiEvents.emit('error', {
              type: 'error',
              title: 'Subscription Expired',
              message: data?.message || 'Your subscription has expired.'
            })
            window.location.href = '/tenant-expired'
            break
          }
          // Any other 403 (a single denied endpoint/poll) must NOT hard-navigate:
          // redirecting would destroy in-progress UI state (e.g. an open POS
          // cart). Surface a toast and let the caller handle the rejection.
          apiEvents.emit('error', {
            type: 'error',
            title: 'Not Authorized',
            message: data?.message || 'You do not have permission to access this resource.',
          })
          break
        }

        case 404:
          return Promise.reject(rejection)

        case 422:
          return Promise.reject(rejection)

        case 429:
        case 500:
        case 502:
        case 503:
        case 504: {
          const meta = STATUS_META[status]
          apiEvents.emit('error', {
            type: 'error',
            title: meta?.title || 'Error',
            message: data?.message || meta?.message || 'An error occurred'
          })
          break
        }

        default:
          console.error(`HTTP Error ${status}:`, data)
      }

      return Promise.reject(rejection)
    } else if (error.request) {
      const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
      const message = timedOut
        ? 'The request took too long. Please try again.'
        : 'Unable to reach the server. Please check your internet connection.'
      if (error.config?.optionalService) {
        return Promise.reject({ message, code: error.code })
      }
      apiEvents.emit('error', {
        type: 'error',
        title: timedOut ? 'Request Timeout' : 'Network Error',
        message,
      })
      return Promise.reject({ message, code: error.code })
    } else {
      return Promise.reject({ message: (error as Error).message })
    }
  }
)

export default api
